// src/context/StopwatchContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode, SetStateAction } from 'react';
import { Light, StopwatchSession } from '../utilities/types';
import stopwatchRepository from '../services/stopwatchRepository';
import SyncManager from './SyncManager';
import { lightService } from '../services/lightService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { calculateFinalBilledTime } from '../utilities/common';
import { toast } from 'react-toastify';

const mapStatus = (state: string): 'running' | 'paused' | 'stopped' => {
    switch (state) {
        case 'active':
            return 'running';
        case 'paused':
            return 'paused';
        default:
            return 'stopped';
    }
}

// Define the shape of the context value
interface StopwatchContextType {
    sessions: StopwatchSession[];
    createSession: (tableId: number) => StopwatchSession;
    start: (id: string, lightDetails: Light) => void;
    pause: (id: string, lightDetails: Light) => void;
    resume: (id: string, lightDetails: Light) => void;
    stop: (id: string, lightDetails: Light) => void;
    moveTable: (id: string, newTableId: number) => void;
    setSessions: React.Dispatch<SetStateAction<StopwatchSession[]>>;
}

// Create the context with a default undefined value
export const StopwatchContext = createContext<StopwatchContextType>({
    sessions: [],
    createSession: () => {
        throw new Error('createSession must be used within a StopwatchProvider');
    },
    start: () => {
        throw new Error('start must be used within a StopwatchProvider');
    },
    pause: () => {
        throw new Error('pause must be used within a StopwatchProvider');
    },
    resume: () => {
        throw new Error('resume must be used within a StopwatchProvider');
    },
    stop: () => {
        throw new Error('stop must be used within a StopwatchProvider');
    },
    moveTable: () => {
        throw new Error('moveTable must be used within a StopwatchProvider');
    },
    setSessions: () => {
        throw new Error('setSessions must be used within a StopwatchProvider');
    }
});

// Create the Provider component
export const StopwatchProvider = ({ children }: { children: ReactNode }) => {
    // The onE AND onLY state for sessions lives here
    const [sessions, setSessions] = useState<StopwatchSession[]>(() => {
        return stopwatchRepository.getAllSessions();
    });

    const isOnline = useOnlineStatus();


    // src/context/StopwatchContext.tsx

    useEffect(() => {
        const fetchAndMergeServerState = async () => {
            if (!isOnline) return;

            try {
                // 1. Get the IDs of ALL sessions we currently have locally.
                const localSessions = stopwatchRepository.getAllSessions();
                const knownIds = localSessions.map(s => s.id);

                // 2. Send these IDs to the server and get back a list of updates and new sessions.
                const serverData = await stopwatchRepository.syncState(knownIds);

                const serverSessions = serverData.map(dto => ({
                    id: dto.client_id,
                    tableId: dto.table_id,
                    state: mapStatus(dto.status),
                    elapsedTime: (dto.hours * 3600 + dto.minutes * 60 + dto.seconds) * 1000,
                    synced: true,
                    lastStartTime: dto.last_start_time ? new Date(dto.last_start_time).toISOString() : undefined,
                }));

                // 3. Perform the final, robust merge.
                setSessions(prevLocalSessions => {
                    // Start with the fresh data from the server.
                    const sessionMap = new Map(serverSessions.map(s => [s.id, s]));

                    // Now, find any local unsynced changes and layer them on top.
                    // This ensures offline work is NEVER lost.
                    const localUnsynced = prevLocalSessions.filter(s => !s.synced);
                    for (const unsyncedSession of localUnsynced) {
                        sessionMap.set(unsyncedSession.id, unsyncedSession);
                    }

                    return Array.from(sessionMap.values());
                });

            } catch (error) {
                console.error("Failed to sync state with server.", error);
            }
        };

        fetchAndMergeServerState();
    }, [isOnline]);

    // The onE AND onLY effect to save to localStorage
    useEffect(() => {
        stopwatchRepository.saveStopwatchSessions(sessions);
    }, [sessions]);

    // All the logic functions are defined here, once.
    const findAndUpdateSession = (id: string, updateFn: (session: StopwatchSession) => StopwatchSession) => {
        setSessions(prevSessions =>
            prevSessions.map(session =>
                session.id === id ? { ...updateFn(session), synced: false } : session
            )
        );
    };

    const createSession = (tableId: number) => {
        const newSession = stopwatchRepository.createStopwatchSession(tableId);
        setSessions(prev => [...prev, newSession]);
        return newSession;
    };

    const start = (id: string, lightDetails: Light) => {
        findAndUpdateSession(id, session => ({
            ...session,
            state: "running",
            lastStartTime: new Date().toISOString(), // Use a new property for clarity
            elapsedTime: 0, // Starting from zero
            synced: false,
        }));

        lightService.controlLights({ ...lightDetails, action: 'on' });
    };

    const stop = (id: string, lightDetails: Light) => {
        findAndUpdateSession(id, session => {
            const now = new Date();
            const lastStart = new Date(session.lastStartTime || now);

            // Calculate the final elapsed time only if it was running
            const finalElapsedTime = session.state === 'running'
                ? session.elapsedTime + (now.getTime() - lastStart.getTime())
                : session.elapsedTime;

            // *** NEW LOGIC IS HERE ***
            // 1. Calculate the final billed time using the helper function.
            const { finalHours, finalMinutes } = calculateFinalBilledTime(finalElapsedTime);

            // 2. Display the result in a toast notification.
            toast.info(`Final time recorded: ${finalHours}h ${finalMinutes}m.`);

            const newElapsedTime = session.elapsedTime + (now.getTime() - lastStart.getTime());

            lightService.controlLights({ ...lightDetails, action: 'off' });
            return {
                ...session,
                state: "stopped",
                lastStartTime: undefined,
                // Depending on if you want to show the final time or reset it
                elapsedTime: session.state === 'running' ? newElapsedTime : session.elapsedTime,
                synced: false,
            };
        });
    };

    const pause = (id: string, lightDetails: Light) => {
        findAndUpdateSession(id, session => {
            if (session.state !== 'running') return session; // Can't pause if not running

            console.log(`Pausing session ${id} at ${session.elapsedTime} ms`);
            const now = new Date();
            const lastStart = new Date(session.lastStartTime || now);
            const newElapsedTime = session.elapsedTime + (now.getTime() - lastStart.getTime());

            console.log(`New elapsed time after pause: ${newElapsedTime} ms`);
            lightService.controlLights({ ...lightDetails, action: 'off' });
            return {
                ...session,
                state: "paused",
                lastStartTime: undefined,
                elapsedTime: newElapsedTime,
                synced: false,
            };
        });
    };

    const resume = (id: string, lightDetails: Light) => {
        findAndUpdateSession(id, session => {
            if (session.state !== 'paused') return session; // Can only resume if paused

            return {
                ...session,
                state: "running",
                lastStartTime: new Date().toISOString(),
                synced: false,
            };
        });
        lightService.controlLights({ ...lightDetails, action: 'on' });
    };

    // New function to move a session to a new table
    const moveTable = (id: string, newTableId: number) => {
        findAndUpdateSession(id, session => ({
            ...session,
            tableId: newTableId,
        }));
    };


    // --- You would copy the full logic for the functions above here ---

    const value = { sessions, createSession, start, pause, resume, stop, moveTable, setSessions };

    return (
        <StopwatchContext.Provider value={value}>
            {children}
            <SyncManager />
        </StopwatchContext.Provider>
    );
};

// Create the hook that components will use
export const useStopwatch = (): StopwatchContextType => {
    return useContext(StopwatchContext);
};

